/**
 * Product Bundle Builder — vanilla JS for Shopify Cart API
 */
(function () {
  'use strict';

  /**
   * @param {number} cents
   * @param {string} format
   */
  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2);
    var withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (format && format.indexOf('amount') !== -1) {
      return format.replace(/\{\{\s*amount\s*\}\}/, withCommas).replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
    }
    return withCommas;
  }

  /**
   * @param {HTMLElement} root
   */
  function init(root) {
    if (!root || root.dataset.bundleInit === '1') return;
    root.dataset.bundleInit = '1';

    var moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    var minCount = parseInt(root.dataset.minCount || '3', 10) || 3;
    var discountPct = parseFloat(root.dataset.discountPercent || '30') || 0;
    var skeletonAnimated = root.dataset.skeletonAnimated === 'true';
    var strings = {};
    (function () {
      var strEl = document.getElementById(root.id + '-strings');
      if (strEl && strEl.textContent) {
        try {
          strings = JSON.parse(strEl.textContent.trim());
        } catch (e) {
          strings = {};
        }
      }
    })();

    var grid = root.querySelector('[data-bundle-grid]');
    var list = root.querySelector('[data-bundle-list]');
    var skeletonWrap = root.querySelector('[data-bundle-skeleton]');
    var subtotalEl = root.querySelector('[data-bundle-subtotal]');
    var subtotalCompareEl = root.querySelector('[data-bundle-subtotal-compare]');
    var discountRow = root.querySelector('[data-bundle-discount-row]');
    var discountBadge = root.querySelector('[data-bundle-discount-badge]');
    var liveRegion = root.querySelector('[data-bundle-live]');
    var cta = root.querySelector('[data-bundle-cta]');
    var ctaTooltip = root.querySelector('[data-bundle-cta-tooltip]');

    /** @type {{ key: string, variantId: number, title: string, sizeLabel: string, priceCents: number, image: string }[]} */
    var items = [];

    function announce(msg) {
      if (liveRegion) {
        liveRegion.textContent = '';
        // eslint-disable-next-line no-unused-expressions
        liveRegion.offsetHeight;
        liveRegion.textContent = msg;
      }
    }

    function getKey(variantId) {
      return 'v-' + variantId;
    }

    function renderList() {
      if (!list) return;

      list.innerHTML = '';

      if (items.length === 0) {
        if (skeletonWrap) {
          skeletonWrap.hidden = false;
          skeletonWrap.setAttribute('aria-hidden', 'false');
        }
        if (subtotalEl) subtotalEl.textContent = formatMoney(0, moneyFormat);
        if (subtotalCompareEl) {
          subtotalCompareEl.textContent = '';
          subtotalCompareEl.hidden = true;
        }
        if (discountRow) discountRow.hidden = true;
        updateCtaState();
        return;
      }

      if (skeletonWrap) {
        skeletonWrap.hidden = true;
        skeletonWrap.setAttribute('aria-hidden', 'true');
      }

      items.forEach(function (item, index) {
        var row = document.createElement('li');
        row.className = 'product-bundle__bundle-item';
        row.setAttribute('data-bundle-line-key', item.key);
        row.style.animationDelay = Math.min(index * 0.05, 0.3) + 's';

        row.innerHTML =
          '<div class="product-bundle__bundle-thumb-wrap">' +
          '<img class="product-bundle__bundle-thumb" src="' +
          escapeAttr(item.image) +
          '" alt="" width="48" height="48" loading="lazy">' +
          '</div>' +
          '<div class="product-bundle__bundle-meta">' +
          '<span class="product-bundle__bundle-name">' +
          escapeHtml(item.title) +
          '</span>' +
          '<span class="product-bundle__bundle-size">' +
          escapeHtml(item.sizeLabel) +
          '</span>' +
          '</div>' +
          '<div class="product-bundle__bundle-price">' +
          formatMoney(item.priceCents, moneyFormat) +
          '</div>' +
          '<button type="button" class="product-bundle__bundle-remove" data-remove-key="' +
          escapeAttr(item.key) +
          '" aria-label="' +
          escapeAttr((strings.removeLabel || 'Remove') + ' ' + item.title) +
          '">' +
          '<span aria-hidden="true">×</span>' +
          '</button>';

        list.appendChild(row);
      });

      var subtotal = items.reduce(function (sum, i) {
        return sum + i.priceCents;
      }, 0);

      var count = items.length;
      var unlocked = count >= minCount && discountPct > 0;
      var finalTotal = unlocked ? Math.round(subtotal * (1 - discountPct / 100)) : subtotal;

      if (subtotalEl) {
        if (unlocked) {
          subtotalEl.textContent = formatMoney(finalTotal, moneyFormat);
        } else {
          subtotalEl.textContent = formatMoney(subtotal, moneyFormat);
        }
      }

      if (subtotalCompareEl) {
        if (unlocked) {
          subtotalCompareEl.textContent = formatMoney(subtotal, moneyFormat);
          subtotalCompareEl.hidden = false;
        } else {
          subtotalCompareEl.textContent = '';
          subtotalCompareEl.hidden = true;
        }
      }

      if (discountRow) {
        discountRow.hidden = !unlocked;
      }

      if (discountBadge && unlocked && strings.discountUnlocked) {
        discountBadge.textContent = strings.discountUnlocked;
      }

      updateCtaState();

      root.querySelectorAll('[data-remove-key]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var key = btn.getAttribute('data-remove-key');
          removeByKey(key);
        });
      });
    }

    function removeByKey(key) {
      items = items.filter(function (i) {
        return i.key !== key;
      });
      renderList();
      announce(strings.removed || 'Item removed from bundle.');
    }

    function updateCtaState() {
      var count = items.length;
      var ok = count >= minCount;
      if (cta) {
        cta.disabled = !ok;
        cta.setAttribute('aria-disabled', ok ? 'false' : 'true');
      }
      if (ctaTooltip) {
        ctaTooltip.textContent = ok ? '' : strings.ctaDisabledReason || '';
        ctaTooltip.hidden = ok;
      }
    }

    function addToBundle(payload) {
      var key = getKey(payload.variantId);
      var existing = items.findIndex(function (i) {
        return i.key === key;
      });
      if (existing !== -1) {
        items.splice(existing, 1);
      }
      items.push({
        key: key,
        variantId: payload.variantId,
        title: payload.title,
        sizeLabel: payload.sizeLabel,
        priceCents: payload.priceCents,
        image: payload.image,
      });
      renderList();
      announce(
        (strings.added || 'Added to bundle:') + ' ' + payload.title + ', ' + payload.sizeLabel
      );
    }

    root.querySelectorAll('[data-size-group] input[type="radio"]').forEach(function (input) {
      input.addEventListener('change', function () {
        var card = input.closest('[data-product-card]');
        if (!card) return;
        var priceEl = card.querySelector('[data-price-display]');
        var cents = parseInt(input.getAttribute('data-variant-price') || '0', 10);
        if (priceEl) {
          priceEl.textContent = formatMoney(cents, moneyFormat);
        }
        var group = input.closest('[data-size-group]');
        if (group) {
          group.querySelectorAll('.product-bundle__size').forEach(function (lab) {
            lab.classList.remove('is-selected');
          });
        }
        var label = input.closest('.product-bundle__size');
        if (label) {
          label.classList.add('is-selected');
        }
      });
    });

    root.querySelectorAll('[data-bundle-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('[data-product-card]');
        if (!card) return;
        var checked = card.querySelector('[data-size-group] input:checked');
        var vid = 0;
        var priceCents = 0;
        var sizeLabel = '';
        if (checked) {
          vid = parseInt(checked.value, 10);
          priceCents = parseInt(checked.getAttribute('data-variant-price') || '0', 10);
          sizeLabel = checked.getAttribute('data-size-label') || '';
        } else {
          vid = parseInt(card.getAttribute('data-default-variant-id') || '0', 10);
          priceCents = parseInt(
            card.getAttribute('data-default-variant-price') || '0',
            10
          );
          sizeLabel = card.getAttribute('data-default-variant-label') || '';
        }
        if (!vid) {
          announce(strings.selectSize || 'Please select a size.');
          return;
        }
        var title = card.getAttribute('data-product-title') || '';
        var image = card.getAttribute('data-product-image') || '';

        addToBundle({
          variantId: vid,
          title: title,
          sizeLabel: sizeLabel,
          priceCents: priceCents,
          image: image,
        });
      });
    });

    if (cta) {
      cta.addEventListener('click', function () {
        if (cta.disabled || items.length < minCount) return;

        var payload = {
          items: items.map(function (i) {
            return { id: i.variantId, quantity: 1 };
          }),
        };

        cta.classList.add('is-loading');
        cta.disabled = true;

        fetch(root.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        })
          .then(function (res) {
            return res.json().then(function (data) {
              if (!res.ok) {
                throw new Error(data.description || data.message || 'Cart error');
              }
              return data;
            });
          })
          .then(function () {
            announce(strings.cartSuccess || 'Bundle added to cart.');
            if (typeof window.dispatchEvent === 'function') {
              document.documentElement.dispatchEvent(
                new CustomEvent('cart:refresh', { bubbles: true })
              );
            }
            window.location.href = root.dataset.cartUrl || '/cart';
          })
          .catch(function (err) {
            announce((strings.cartError || 'Error:') + ' ' + (err.message || String(err)));
            cta.disabled = false;
          })
          .finally(function () {
            cta.classList.remove('is-loading');
            updateCtaState();
          });
      });
    }

    if (skeletonWrap && skeletonAnimated) {
      skeletonWrap.classList.add('product-bundle__skeleton--animated');
    }

    renderList();
  }

  function escapeHtml(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  function escapeAttr(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
  }

  document.querySelectorAll('[data-product-bundle-root]').forEach(init);
  document.addEventListener('shopify:section:load', function (ev) {
    if (ev.target) {
      ev.target.querySelectorAll('[data-product-bundle-root]').forEach(init);
    }
  });
})();
